import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { DialogType } from '../../../common/enums.js';
import { t } from '../../../common/i18n/index.js';
import { CREATE_NEW_CONTACT_OPTION_VALUE } from '../../../common/react-hook-form/fields/ContactDropdown.js';
import { ChangeSubscriptionContactDialog } from '../../ChangeSubscriptionContactDialog/ChangeSubscriptionContactDialog.js';
import { PurposeOfUse } from '../../PurposeOfUse/PurposeOfUse.js';
import { includesVoiceMail } from '../../SubscriptionAdditionalService/subscriptionAddOnUtils.js';
import { UserNumberPrivacy } from '../../UserAndNumberPublicity/components/UserNumberPrivacy.js';
import type { PurposeOfUseOrContact } from '../../../common/types/subscription.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { Subscription } from '../../../generated/api/subscription.js';
import type { SubscriptionPbxDetails } from '../../../generated/api/subscriptionPbxDetails.js';

interface UserInfoFieldsProps {
  companyName?: string;
  contacts: Contact[];
  isCostCenterMandatory: boolean;
  isEmailMandatory: boolean;
  isPhoneNumberMandatory: boolean;
  onChangeContact: (resetField: (name: string, options?: Record<string, boolean>) => void) => void;
  onCreateContact: () => void;
  pbxType?: SubscriptionPbxDetails.PbxTypeEnum;
  purposeOfUseOrContact: PurposeOfUseOrContact;
  subscription: Subscription;
  directoryListingDisabled?: boolean;
}

export const SubscriptionUserInfoFields = ({
  companyName,
  contacts,
  isCostCenterMandatory,
  isEmailMandatory,
  isPhoneNumberMandatory,
  onChangeContact,
  onCreateContact,
  pbxType,
  purposeOfUseOrContact,
  subscription,
  directoryListingDisabled,
}: UserInfoFieldsProps) => {
  const { resetField } = useFormContext();
  const [showChangedContactDialog, setShowChangedContactDialog] = useState(false);

  const handleContactChange = (
    selectedContactId: string,
    formResetField: (name: string, options?: Record<string, boolean>) => void
  ) => {
    const isDifferentContact =
      selectedContactId &&
      selectedContactId !== purposeOfUseOrContact?.contactId &&
      selectedContactId !== CREATE_NEW_CONTACT_OPTION_VALUE;
    if (isDifferentContact) {
      setShowChangedContactDialog(true);
    }
    onChangeContact(formResetField);
  };

  const handleDialogConfirm = () => {
    setShowChangedContactDialog(false);
  };

  const handleDialogCancel = () => {
    setShowChangedContactDialog(false);
    resetField('contactId');
    resetField('costCenter');
    resetField('employeeNumber');
    resetField('directoryListingMobileNumberPublicity');
    resetField('directoryListingCorporateNumberPublicity');
  };

  return (
    <>
      {showChangedContactDialog && (
        <ChangeSubscriptionContactDialog
          type={DialogType.CHANGE_SUBSCRIPTION_CONTACT}
          subscriptionType={subscription.subscriptionType}
          showOmaLaiteLaskuWarning={Boolean(subscription.details?.device?.omaLaiteLasku)}
          showVoiceMailWarning={includesVoiceMail(subscription)}
          onConfirm={handleDialogConfirm}
          onCancel={handleDialogCancel}
        />
      )}

      <PurposeOfUse
        companyName={companyName}
        contacts={contacts}
        isCostCenterMandatory={isCostCenterMandatory}
        isPhoneNumberMandatory={isPhoneNumberMandatory}
        isEmailMandatory={isEmailMandatory}
        onChangeContact={handleContactChange}
        onCreateUser={onCreateContact}
        showHeaderAndDescription={false}
        useCreateContactModal={true}
        selectorLabel={t.WPXQ('Select user or purpose of use')}
        showCostCenterAndEmployeeNumberTitle={false}
      />
      <UserNumberPrivacy
        pbxType={pbxType}
        corporateDirectoryDetails={purposeOfUseOrContact.directoryListingCorporateNumberPublicity?.pbxDirectoryDetails}
        voiceDirectoryDetails={purposeOfUseOrContact.directoryListingMobileNumberPublicity?.voiceDirectoryDetails}
        disabled={directoryListingDisabled}
      />
    </>
  );
};
